import {
  FormControl,
  FormLabel,
  Input,
  ModalBody,
  ModalFooter,
  Text,
} from '@chakra-ui/react';
import { verifyTOTPSetup, updateMFAPreference } from 'aws-amplify/auth';
import React, { useState } from 'react';
import { Button } from 'src/components/atoms/Button';
import { Modal } from 'src/components/atoms/Modal';
import { TotpEnrollment } from 'src/components/molecules/TotpEnrollment';

type TotpSetupModalProps = {
  isOpen: boolean;
  onClose: () => void;
  header: string;
  setupUri: string | null;
  setupSecret: string | null;
  onVerified: () => void;
};

/**
 * Modal that completes authenticator app registration: shows the QR
 * code / secret and verifies the first TOTP code. The Cognito setup
 * session is started by the parent (setUpTOTP) before opening.
 */
export const TotpSetupModal: React.FC<TotpSetupModalProps> = ({
  isOpen,
  onClose,
  header,
  setupUri,
  setupSecret,
  onVerified,
}) => {
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setCode('');
    setError(null);
    onClose();
  };

  const handleVerify = async () => {
    if (!code.trim()) {
      setError('Please enter the code from your authenticator app.');
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      await verifyTOTPSetup({ code: code.trim() });
      // Keep email MFA enabled alongside TOTP so both methods are
      // selectable at sign-in
      await updateMFAPreference({ totp: 'ENABLED', email: 'ENABLED' });
      setCode('');
      onVerified();
    } catch (verifyError) {
      let errorMessage = 'Please try again or contact support.';
      if (
        verifyError &&
        typeof verifyError === 'object' &&
        'name' in verifyError
      ) {
        if (
          verifyError.name === 'EnableSoftwareTokenMFAException' ||
          verifyError.name === 'CodeMismatchException'
        ) {
          errorMessage = 'Invalid code. Please try again.';
        } else if (
          'message' in verifyError &&
          typeof verifyError.message === 'string'
        ) {
          errorMessage = verifyError.message;
        }
      }
      setError(errorMessage);
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      maxWidth="480px"
      header={header}>
      <ModalBody>
        <TotpEnrollment setupUri={setupUri} secret={setupSecret} />
        <FormControl mt={2} isInvalid={Boolean(error)}>
          <FormLabel fontSize="sm" fontWeight="medium">
            Enter the 6-digit code from the app
          </FormLabel>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            inputMode="numeric"
            autoComplete="one-time-code"
          />
          {error && (
            <Text fontSize="sm" color="red.500" mt={2}>
              {error}
            </Text>
          )}
        </FormControl>
      </ModalBody>
      <ModalFooter>
        <Button
          buttonName="cancel-totp-setup"
          variant="ghost"
          mr={3}
          onClick={handleClose}>
          Cancel
        </Button>
        <Button
          buttonName="verify-totp-setup"
          variant="primary"
          onClick={handleVerify}
          isLoading={isVerifying}>
          Verify
        </Button>
      </ModalFooter>
    </Modal>
  );
};
